/**
 * transmissionService.js
 * Enveloppes de transmission PF-03 (spec crypto v1.1 §3.1, §3.2, §4.1, §4.2,
 * Annexe B ; DEC-036).
 *
 * Par bénéficiaire :
 *   - K_trans = CSPRNG(32), jamais dérivée d'un code ;
 *   - enveloppe = payload JSON chiffré sous K_trans,
 *     AAD "safeheir-transmission-envelope-v1|" + envelope_id ;
 *   - K_trans enveloppée sous la MK, AAD "safeheir-ktrans-mk-v1|" + beneficiary_id ;
 *   - partage additif : part_serveur stockée, part titulaire affichée en 24 mots.
 *
 * Préparation (D4) scindée en deux temps : buildTransmission() construit tout
 * en mémoire, commitTransmission() insère UNIQUEMENT après la confirmation
 * des 3 mots. Onglet fermé entre les deux = rien en base.
 *
 * Resynchronisation (D3) : réutilise la MÊME K_trans et recopie part_serveur,
 * le code papier reste valide. Détection par contenu uniquement ; jamais de
 * comparaison avec beneficiaries.updated_at ni envelope.created_at (K116).
 *
 * K_trans et la part titulaire ne sont jamais persistées ni journalisées.
 */

import { supabase } from './supabaseClient';
import {
  AAD_KTRANS_MK_V1,
  AAD_TRANSMISSION_ENVELOPE_V1,
  bytesToMnemonicFr,
  decryptBytes,
  decryptData,
  encryptBytes,
  encryptData,
  generateRandomBytes,
  importAesKeyFromBytes,
  splitSecret32,
} from './cryptoService';
import { getAll as getVaultItems } from './vaultService';
import { isRevoked, setReleasePath } from './beneficiaryService';
import { DEFAULT_RELEASE_PATH } from '../config/releasePathConfig';

export const ENVELOPE_VERSION = 'safeheir-transmission-envelope-v1';

/**
 * vault_snapshot_at quand aucun item ne correspond aux catégories (K118) :
 * valeur fixe, jamais une horloge client dans une colonne serveur.
 */
export const EPOCH_SNAPSHOT = '1970-01-01T00:00:00.000Z';

const TABLE = 'transmission_envelopes';

/**
 * Statuts terminaux : aucune enveloppe ne peut être créée (trigger CP1).
 * Exporté pour l'UI (CP3.1) : source unique côté transmission.
 * @param {{ status?: string } | null | undefined} beneficiary
 * @returns {boolean}
 */
export const isTerminal = (beneficiary) =>
  isRevoked(beneficiary) || ['transmitted', 'expired'].includes(beneficiary?.status);

const sortedCategories = (categories) => [...(categories || [])].sort();

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/**
 * Horodatage timestamptz → microsecondes depuis l'epoch. Précision
 * microseconde conservée (Date.parse tronque à la milliseconde).
 * @param {string} ts
 * @returns {number}
 */
function toMicros(ts) {
  const seconds = Math.floor(Date.parse(ts) / 1000);
  const fraction = (ts.match(/:\d{2}\.(\d+)/)?.[1] || '').padEnd(6, '0').slice(0, 6);
  return seconds * 1e6 + Number(fraction);
}

/**
 * Item le plus récent (updated_at serveur, trigger update_updated_at_column).
 * @param {Array<{ updated_at: string }>} items
 * @returns {string|null} updated_at d'origine, ou null si aucun item.
 */
function latestUpdatedAt(items) {
  let latest = null;
  for (const item of items) {
    if (latest === null || toMicros(item.updated_at) > toMicros(latest)) latest = item.updated_at;
  }
  return latest;
}

const filterItems = (vaultItems, categories) =>
  vaultItems
    .filter((item) => categories.includes(item.category))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

/**
 * Charge le coffre déchiffré. Refuse un coffre partiellement déchiffré :
 * une transmission ne doit jamais omettre silencieusement un item.
 * @returns {Promise<{ items?: Array, error?: string }>}
 */
async function loadVault(masterKey) {
  const result = await getVaultItems(masterKey);
  if (!result.success) return { error: result.error || 'Lecture du coffre impossible.' };
  if (result.failedCount > 0) {
    return { error: `${result.failedCount} élément(s) du coffre indéchiffrable(s) : transmission non mise à jour.` };
  }
  return { items: result.data };
}

// ─── S1 ────────────────────────────────────────────────────────────────────

/**
 * Construit le payload clair d'une enveloppe bénéficiaire. Contient
 * uniquement des items du coffre, jamais de donnée de profil du titulaire.
 * @param {object} params
 * @param {{ id: string, access_categories?: string[] }} params.beneficiary
 * @param {Array<{ id, category, data, updated_at }>} params.vaultItems - Déchiffrés.
 * @returns {object}
 */
export function buildEnvelopePayload({ beneficiary, vaultItems }) {
  const categories = sortedCategories(beneficiary.access_categories);
  return {
    version: ENVELOPE_VERSION,
    beneficiary_id: beneficiary.id,
    categories,
    generated_at: new Date().toISOString(),
    items: filterItems(vaultItems, categories).map(({ id, category, data, updated_at }) => ({
      id,
      category,
      data,
      updated_at,
    })),
  };
}

// ─── S4 ────────────────────────────────────────────────────────────────────

/**
 * Enveloppe active d'un bénéficiaire (SELECT via RLS titulaire).
 * @param {string} beneficiaryId
 * @returns {Promise<{ prepared: boolean, activeEnvelope: object|null, error?: string }>}
 */
export async function getEnvelopeStatus(beneficiaryId) {
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('beneficiary_id', beneficiaryId)
      .eq('status', 'active')
      .maybeSingle();
    if (error) return { prepared: false, activeEnvelope: null, error: error.message };
    return { prepared: !!data, activeEnvelope: data || null };
  } catch (e) {
    return { prepared: false, activeEnvelope: null, error: e.message };
  }
}

/**
 * Garde commune build/commit : statut non terminal et aucune enveloppe active.
 * @returns {Promise<string|null>} Message de refus, ou null si autorisé.
 */
async function preparationRefusal(beneficiary) {
  if (isTerminal(beneficiary)) {
    return 'Transmission impossible : ce bénéficiaire est révoqué, transmis ou expiré.';
  }
  const status = await getEnvelopeStatus(beneficiary.id);
  if (status.error) return `Vérification de la transmission impossible : ${status.error}`;
  if (status.prepared) return 'La transmission de ce bénéficiaire est déjà préparée.';
  return null;
}

// ─── S2a ───────────────────────────────────────────────────────────────────

/**
 * Prépare une transmission EN MÉMOIRE. Aucune écriture en base.
 * @param {object} params
 * @param {object} params.beneficiary - Bénéficiaire déchiffré (id, status, access_categories).
 * @param {CryptoKey} params.masterKey
 * @returns {Promise<{ success: boolean, envelopeRow?: object, holderMnemonic?: string, error?: string }>}
 */
export async function buildTransmission({ beneficiary, masterKey }) {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé.' };
  let kTransBytes = null;
  let serverShare = null;
  let holderShare = null;
  try {
    const refusal = await preparationRefusal(beneficiary);
    if (refusal) return { success: false, error: refusal };

    const vault = await loadVault(masterKey);
    if (vault.error) return { success: false, error: vault.error };

    const envelopeId = crypto.randomUUID();
    kTransBytes = generateRandomBytes(32);
    const kTransKey = await importAesKeyFromBytes(kTransBytes);

    const payload = buildEnvelopePayload({ beneficiary, vaultItems: vault.items });
    const { ciphertext, iv } = await encryptData(
      payload,
      kTransKey,
      AAD_TRANSMISSION_ENVELOPE_V1(envelopeId)
    );

    ({ serverShare, holderShare } = splitSecret32(kTransBytes));
    const wrapped = await encryptBytes(kTransBytes, masterKey, AAD_KTRANS_MK_V1(beneficiary.id));

    const envelopeRow = {
      id: envelopeId,
      beneficiary_id: beneficiary.id,
      enveloppe_chiffree: ciphertext,
      enveloppe_iv: iv,
      part_serveur: bytesToBase64(serverShare),
      k_trans_wrapped_mk: wrapped.ciphertext,
      k_trans_wrapped_iv: wrapped.iv,
      key_version: 1,
      vault_snapshot_at: latestUpdatedAt(payload.items) || EPOCH_SNAPSHOT,
    };
    const holderMnemonic = bytesToMnemonicFr(holderShare);

    return { success: true, envelopeRow, holderMnemonic };
  } catch (e) {
    return { success: false, error: e.message };
  } finally {
    kTransBytes?.fill(0);
    serverShare?.fill(0);
    holderShare?.fill(0);
  }
}

// ─── S2b ───────────────────────────────────────────────────────────────────

/**
 * Insère l'enveloppe préparée. À appeler UNIQUEMENT après confirmation des
 * 3 mots. Relit le statut en base : il a pu changer depuis buildTransmission().
 * Pose release_path (setReleasePath) avant l'INSERT ; échec = aucun INSERT.
 * Non atomique : si l'INSERT échoue ensuite, un release_path posé sans
 * enveloppe est sans effet (le badge reste « Transmission non préparée »).
 * @param {object} params
 * @param {{ id: string }} params.beneficiary
 * @param {object} params.envelopeRow - Issue de buildTransmission().
 * @param {string} params.releasePath - Chemin choisi dans le parcours (DEC-037).
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function commitTransmission({ beneficiary, envelopeRow, releasePath }) {
  try {
    if (!envelopeRow || envelopeRow.beneficiary_id !== beneficiary.id) {
      return { success: false, error: 'Enveloppe incohérente avec le bénéficiaire.' };
    }
    const { data: current, error: readError } = await supabase
      .from('beneficiaries')
      .select('status')
      .eq('id', beneficiary.id)
      .maybeSingle();
    if (readError || !current) return { success: false, error: 'Bénéficiaire introuvable.' };

    const refusal = await preparationRefusal({ ...beneficiary, status: current.status });
    if (refusal) return { success: false, error: refusal };

    const pathResult = await setReleasePath(beneficiary.id, releasePath);
    if (!pathResult.success) return { success: false, error: pathResult.error };

    const { error } = await supabase.from(TABLE).insert(envelopeRow);
    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
}

// ─── Overview (PF-03 CP3.1, avertissements dashboard) ──────────────────────

/**
 * Vue d'ensemble des transmissions SANS Master Key : catégories en clair des
 * vault_items (RLS, comme getDistinctCategories), aucun déchiffrement (K69).
 * Jamais d'exception ; toute erreur → { byId: {}, error }.
 * @param {object} params
 * @param {Array<object>} params.beneficiaries
 * @returns {Promise<{ byId: Object<string, { prepared: boolean, itemCount: number, releasePath: string, hasTrustedContact: boolean }>, error?: string }>}
 */
export async function getTransmissionOverview({ beneficiaries }) {
  try {
    const { data: rows, error } = await supabase.from('vault_items').select('id, category');
    if (error) return { byId: {}, error: error.message };

    const byId = {};
    for (const beneficiary of (beneficiaries || []).filter((b) => !isTerminal(b))) {
      const status = await getEnvelopeStatus(beneficiary.id);
      if (status.error) return { byId: {}, error: status.error };
      const categories = beneficiary.access_categories || [];
      byId[beneficiary.id] = {
        prepared: status.prepared,
        itemCount: (rows || []).filter((r) => categories.includes(r.category)).length,
        releasePath: beneficiary.release_path || DEFAULT_RELEASE_PATH,
        // Hash en clair (CP3.2) : présence du contact connue sans déchiffrement.
        hasTrustedContact: !!beneficiary.trusted_contact_email_hash,
      };
    }
    return { byId };
  } catch (e) {
    return { byId: {}, error: e.message };
  }
}

// ─── S3 ────────────────────────────────────────────────────────────────────

/**
 * Resynchronise l'enveloppe active d'un bénéficiaire si le coffre ou ses
 * catégories ont changé. Lève en cas d'échec (collecté par l'appelant).
 * @returns {Promise<'resynced'|'skipped'>}
 */
async function resyncOne(beneficiary, vaultItems, masterKey) {
  const status = await getEnvelopeStatus(beneficiary.id);
  if (status.error) throw new Error(status.error);
  const envelope = status.activeEnvelope;
  if (!envelope) return 'skipped';

  const kTransBytes = await decryptBytes(
    envelope.k_trans_wrapped_mk,
    envelope.k_trans_wrapped_iv,
    masterKey,
    AAD_KTRANS_MK_V1(beneficiary.id)
  );
  try {
    const kTransKey = await importAesKeyFromBytes(kTransBytes);
    const previous = await decryptData(
      envelope.enveloppe_chiffree,
      envelope.enveloppe_iv,
      kTransKey,
      AAD_TRANSMISSION_ENVELOPE_V1(envelope.id)
    );

    const categories = sortedCategories(beneficiary.access_categories);
    const itemsNow = filterItems(vaultItems, categories);
    const latestNow = latestUpdatedAt(itemsNow);
    const idsNow = itemsNow.map((i) => i.id);
    const idsBefore = previous.items.map((i) => i.id).sort();

    const staleVault =
      (latestNow !== null && toMicros(latestNow) > toMicros(envelope.vault_snapshot_at)) ||
      !sameList(idsNow, idsBefore);
    const staleCategories = !sameList(sortedCategories(previous.categories), categories);

    if (!staleVault && !staleCategories) return 'skipped';

    const envelopeId = crypto.randomUUID();
    const payload = buildEnvelopePayload({ beneficiary, vaultItems });
    const { ciphertext, iv } = await encryptData(
      payload,
      kTransKey,
      AAD_TRANSMISSION_ENVELOPE_V1(envelopeId)
    );

    const { error } = await supabase.from(TABLE).insert({
      id: envelopeId,
      beneficiary_id: beneficiary.id,
      enveloppe_chiffree: ciphertext,
      enveloppe_iv: iv,
      part_serveur: envelope.part_serveur,
      k_trans_wrapped_mk: envelope.k_trans_wrapped_mk,
      k_trans_wrapped_iv: envelope.k_trans_wrapped_iv,
      key_version: envelope.key_version,
      vault_snapshot_at: latestNow || EPOCH_SNAPSHOT,
    });
    if (error) throw new Error(error.message);
    return 'resynced';
  } finally {
    kTransBytes.fill(0);
  }
}

/**
 * Resynchronisation automatique au déverrouillage (D3). Jamais bloquante :
 * aucune exception, erreurs collectées. Coffre chargé une seule fois.
 * @param {object} params
 * @param {Array<object>} params.beneficiaries - Bénéficiaires déchiffrés.
 * @param {CryptoKey} params.masterKey
 * @returns {Promise<{ resynced: number, skipped: number, errors: Array<{ beneficiary_id?: string, error: string }> }>}
 */
export async function resyncEnvelopes({ beneficiaries, masterKey }) {
  const report = { resynced: 0, skipped: 0, errors: [] };
  try {
    if (!masterKey) return report;
    const candidates = (beneficiaries || []).filter((b) => !isTerminal(b));
    report.skipped = (beneficiaries || []).length - candidates.length;
    if (candidates.length === 0) return report;

    const vault = await loadVault(masterKey);
    if (vault.error) {
      report.errors.push({ error: vault.error });
      return report;
    }

    for (const beneficiary of candidates) {
      try {
        const outcome = await resyncOne(beneficiary, vault.items, masterKey);
        report[outcome] += 1;
      } catch (e) {
        report.errors.push({ beneficiary_id: beneficiary.id, error: e.message });
      }
    }
  } catch (e) {
    report.errors.push({ error: e.message });
  }
  return report;
}
